import { Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { client } from '@/api/client'
import { AuthLayout } from '@/components/AuthLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import i18n from '@/i18n/config'
import { useAuthStore } from '@/stores/auth'

interface LoginForm {
  username: string
  password: string
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('auth')
  const { setToken, setParent } = useAuthStore()
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>()

  const onSubmit = async (data: LoginForm) => {
    try {
      const response = await client.post('/auth/login/', data)
      const { token, parent } = response.data
      setToken(token)
      setParent(parent)

      if (parent.language_preference) {
        i18n.changeLanguage(parent.language_preference)
      }

      if (!parent.is_email_verified) {
        navigate('/verification-pending', { replace: true })
        return
      }

      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    } catch (err: any) {
      setError(err.response?.data?.error || t('login.error'))
    }
  }

  return (
    <AuthLayout>
      {/* Logo */}
      <div className="flex justify-center mb-8">
        <img src="/images/logo.png" alt="The Ranch" className="h-20 w-auto" />
      </div>

      {/* Login Card */}
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t('login.submitButton')}</CardTitle>
          <CardDescription>{t('login.emailPlaceholder')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            {error && (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email-input">{t('login.emailLabel')}</Label>
              <Input
                id="email-input"
                {...register('username', { required: t('login.emailRequired') })}
                type="email"
                autoComplete="off"
                spellCheck="false"
                placeholder={t('login.emailPlaceholder')}
              />
              {errors.username && (
                <p className="text-xs text-destructive">{errors.username.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password-input">{t('login.passwordLabel')}</Label>
              <div className="relative">
                <Input
                  id="password-input"
                  {...register('password', { required: t('login.passwordRequired') })}
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder={t('login.passwordPlaceholder')}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full">
              {t('login.submitButton')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Register link */}
      <Card className="mt-4">
        <CardContent className="py-4 text-center text-sm">
          {t('login.noAccount')}{' '}
          <Link to="/register" className="text-primary font-medium hover:underline">
            {t('login.registerLink')}
          </Link>
        </CardContent>
      </Card>

      {/* Reset password link */}
      <Card className="mt-3">
        <CardContent className="py-4 text-center text-sm">
          {t('login.forgotPassword')}{' '}
          <Link to="/reset-password" className="text-primary font-medium hover:underline">
            {t('login.resetPasswordLink')}
          </Link>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
